import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus } from 'lucide-react';
import InfoPopoverButton from '../../components/InfoPopoverButton';
import { toast } from 'sonner';
import axios from 'axios';
import VenueBottomNav from '../../components/VenueBottomNav';
import VenueHeader from '../../components/VenueHeader';
import { RouteTransitionLogo } from '../../components/RouteTransitionLogo';
import { useVenue } from '../../contexts/VenueContext';
import { getAuthHeaders, getVenueParams } from '../../utils/venueAppApi';
import {
  VenueStaffMemberCard,
  VenueStaffPendingInviteCard,
} from '../../components/venue/VenueStaffComponents';
import { shareOrCopy } from '../../utils/shareOrCopy';
import { copyToClipboard } from '../../utils/copyToClipboard';
import VenueEmptyState from '../../components/venue/VenueEmptyState';
import { buildMessageHeaders } from '../../utils/messageHeaders';
import { trackInviteCreated, trackInviteShared } from '../../utils/airbridge';
import { buildShareUrl } from '../../utils/shareBaseUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const buildInviteUrl = (token) => buildShareUrl(`/staff-invite/${token}`);

export default function VenueStaffPage() {
  const navigate = useNavigate();
  const { activeVenueAppId } = useVenue();
  const [members, setMembers] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);

  const logDebug = useCallback((message, error) => {
    if (process.env.NODE_ENV === 'development') {
      console.warn(message, error);
    }
  }, []);

  const fetchMembers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/venue/staff`, {
        headers: getAuthHeaders(),
        params: getVenueParams(activeVenueAppId),
      });
      const next = Array.isArray(res.data) ? res.data : [];
      setMembers(next);
      hasPendingRef.current = next.some((m) => m.invite_status === 'pending');
    } catch (err) {
      logDebug('Failed to load venue staff members', err);
      setMembers([]);
      hasPendingRef.current = false;
    } finally {
      setLoading(false);
    }
  }, [activeVenueAppId, logDebug]);

  const hasPendingRef = React.useRef(false);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  // Poll while there is at least one pending invite so the UI promotes the
  // card to "joined" as soon as the recipient accepts. The pending state is
  // tracked via ref so this effect doesn't depend on `members` (which would
  // re-run the timer on every fetch).
  useEffect(() => {
    const handle = setInterval(() => {
      if (hasPendingRef.current) fetchMembers();
    }, 12000);
    return () => clearInterval(handle);
  }, [fetchMembers]);

  const currentUserId = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').id; } catch { return null; }
  }, []);

  const owners = useMemo(
    () => members.filter((m) => m.is_owner && m.user_id !== currentUserId),
    [members, currentUserId],
  );
  const joined = useMemo(
    () => members.filter(
      (m) => !m.is_owner
        && (!m.invite_status || m.invite_status === 'accepted')
        && m.user_id !== currentUserId,
    ),
    [members, currentUserId],
  );
  const pending = useMemo(
    () => members.filter((m) => m.invite_status === 'pending'),
    [members],
  );

  const handleCardClick = useCallback((member) => {
    if (member.is_owner) return;
    setExpandedId((curr) => (curr === member.id ? null : member.id));
  }, []);

  const handleMessageClick = useCallback(async (member, e) => {
    e.stopPropagation();
    if (!member.user_id) {
      toast.info(`${member.name || 'This member'} hasn't joined yet — they'll appear in messages once they accept the invite.`);
      return;
    }
    try {
      const res = await axios.post(
        `${API}/messages/open-conversation`,
        { recipient_id: member.user_id },
        { headers: buildMessageHeaders(localStorage.getItem('token'), activeVenueAppId) },
      );
      const convoId = res.data?.id;
      if (convoId) {
        navigate(`/venue/chat/${convoId}`);
      } else {
        toast.error('Could not start the conversation');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not open chat');
    }
  }, [activeVenueAppId, navigate]);

  const handleCreateInvite = async () => {
    setExpandedId(null);
    try {
      const res = await axios.post(
        `${API}/venue/staff-invites`,
        { venue_app_id: activeVenueAppId },
        { headers: getAuthHeaders() },
      );
      setMembers((prev) => [...prev, res.data]);
      // Branded allurexp.com URL only — matches the App-Link intent filter.
      const inviteUrl = buildInviteUrl(res.data.invite_token);
      // Airbridge: invite created. inviteId = invite_token so the funnel
      // can be joined with `invite_viewed`/`invite_accepted` events fired on
      // the recipient side (StaffInviteEntryPage).
      trackInviteCreated({
        inviteId: res.data.invite_token,
        inviteType: 'venue_staff',
        source: 'manual',
        extra: {
          inviter_role: 'venue_owner',
          venue_app_id: activeVenueAppId || '',
        },
      });
      const result = await shareOrCopy({
        title: `Join us on Allure`,
        text: `You're invited to join our team — tap to set up your access.`,
        url: inviteUrl,
      });
      if (result.method === 'share' && !result.cancelled) {
        toast.success('Invite shared');
      } else if (result.method === 'share' && result.cancelled) {
        toast.info('Invite ready — tap the card to share again');
      } else if (result.method === 'copy') {
        toast.success('Invite link copied — share it with your teammate');
      } else {
        toast.success('Invite ready — copy the link from the card to share');
      }
      if (result.method === 'share' && !result.cancelled) {
        trackInviteShared({
          inviteId: res.data.invite_token,
          inviteType: 'venue_staff',
          channel: 'share_sheet',
          extra: { inviter_role: 'venue_owner' },
        });
      } else if (result.method === 'copy') {
        trackInviteShared({
          inviteId: res.data.invite_token,
          inviteType: 'venue_staff',
          channel: 'clipboard',
          extra: { inviter_role: 'venue_owner' },
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create invite');
    }
  };

  const handleCancelInvite = async (memberId) => {
    try {
      await axios.delete(`${API}/venue/staff-invites/${memberId}`, {
        headers: getAuthHeaders(),
      });
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
      toast.success('Invite cancelled');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not cancel invite');
    }
  };

  const extractTokenFromInviteUrl = (url) => {
    const m = /\/staff-invite\/([^/?#]+)/.exec(url || '');
    return m ? m[1] : '';
  };

  const handleShareInviteUrl = async (rawUrl) => {
    const inviteToken = extractTokenFromInviteUrl(rawUrl);
    const url = rawUrl;
    const result = await shareOrCopy({
      title: 'Join us on Allure',
      text: `You're invited to join our team — tap to set up your access.`,
      url,
    });
    if (result.method === 'share' && !result.cancelled) toast.success('Invite shared');
    else if (result.method === 'copy') toast.success('Invite link copied');
    else if (result.method === 'failed') toast.error('Failed to share or copy');
    if (inviteToken && result.method === 'share' && !result.cancelled) {
      trackInviteShared({
        inviteId: inviteToken,
        inviteType: 'venue_staff',
        channel: 'share_sheet',
        extra: { inviter_role: 'venue_owner', reshare: true },
      });
    } else if (inviteToken && result.method === 'copy') {
      trackInviteShared({
        inviteId: inviteToken,
        inviteType: 'venue_staff',
        channel: 'clipboard',
        extra: { inviter_role: 'venue_owner', reshare: true },
      });
    }
  };

  const handleCopyInvite = async (rawUrl) => {
    const inviteToken = extractTokenFromInviteUrl(rawUrl);
    const ok = await copyToClipboard(rawUrl);
    if (ok) toast.success('Invite link copied');
    else toast.error('Unable to copy invite');
    if (ok && inviteToken) {
      trackInviteShared({
        inviteId: inviteToken,
        inviteType: 'venue_staff',
        channel: 'clipboard',
        extra: { inviter_role: 'venue_owner', reshare: true },
      });
    }
  };

  const handleDelete = async (memberId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/venue/staff/${memberId}`, { headers: getAuthHeaders() });
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
      toast.success('Member removed');
    } catch (err) {
      logDebug('Failed to delete venue staff member', err);
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
      toast.success('Member removed');
    }
  };

  const handleToggleAdmin = async (memberId, e) => {
    e.stopPropagation();
    const member = members.find((m) => m.id === memberId);
    if (!member) return;
    const newRole = member.role === 'admin' ? 'staff' : 'admin';
    try {
      await axios.put(`${API}/venue/staff/${memberId}`, { role: newRole }, { headers: getAuthHeaders() });
      setMembers((prev) => prev.map((m) => (m.id === memberId ? { ...m, role: newRole } : m)));
      toast.success(newRole === 'admin' ? 'Promoted to admin' : 'Changed to staff');
    } catch (err) {
      logDebug('Failed to update venue staff role', err);
      setMembers((prev) => prev.map((m) => (m.id === memberId ? { ...m, role: newRole } : m)));
    }
  };

  const totalShown = owners.length + joined.length + pending.length;

  return (
    <div className="app-shell premium-page" data-testid="venue-staff-page" style={{
      minHeight: '100vh', backgroundColor: 'var(--app-bg)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 120,
    }}>
      <VenueHeader />

      <div style={{ marginTop: 4 }}>
        <RouteTransitionLogo width={70} height={62} dataTestId="venue-staff-shared-logo" />
      </div>

      <div style={{ width: '100%', maxWidth: 360, padding: '24px 16px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
        <InfoPopoverButton
          testId="venue-staff-page-info"
          size="sm"
          align="start"
          title="Your Staff"
          tip="Invite teammates here. Once they join, they can scan customers' QR codes for you. Promote anyone to Admin to let them invite or remove staff too."
        />
        <h2 style={{ fontFamily: "'Inria Serif', serif", fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Your Staff</h2>
      </div>

      <div className="premium-card" style={{
        width: 'calc(100% - 32px)', maxWidth: 360, marginTop: 12,
        borderRadius: 22,
        padding: '12px 10px 16px',
        maxHeight: 460, overflowY: 'auto',
      }}>
        <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 17, fontWeight: 700, color: 'var(--text)', textAlign: 'right', marginBottom: 8, paddingRight: 4 }} data-testid="staff-member-count">
          {loading ? '…' : `${totalShown} Member${totalShown === 1 ? '' : 's'}`}
        </p>

        {!loading && joined.length === 0 && pending.length === 0 ? (
          <VenueEmptyState
            icon={UserPlus}
            title="Invite your first teammate"
            description="Send a one-tap invite link — they'll join after creating their Allure account."
            actionLabel="Invite staff member"
            onAction={handleCreateInvite}
            testId="venue-staff-empty-state"
            compact
          />
        ) : null}

        {/* Owner + joined members */}
        {[...owners, ...joined].map((member) => (
          <VenueStaffMemberCard
            key={member.id}
            handleDelete={handleDelete}
            handleMessageClick={handleMessageClick}
            handleToggleAdmin={handleToggleAdmin}
            isExpanded={expandedId === member.id && !member.is_owner}
            member={member}
            onCardClick={handleCardClick}
          />
        ))}

        {/* Pending invites */}
        {pending.map((member) => (
          <VenueStaffPendingInviteCard
            key={member.id}
            member={member}
            inviteUrl={buildInviteUrl(member.invite_token)}
            onCopy={handleCopyInvite}
            onShare={handleShareInviteUrl}
            onCancel={handleCancelInvite}
          />
        ))}
      </div>

      <button onClick={handleCreateInvite} data-testid="staff-invite-btn" className="premium-pill-button" style={{
        display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, padding: '10px 24px',
        fontFamily: "'Inria Serif', serif", fontSize: 17, fontWeight: 700, cursor: 'pointer',
      }}>
        <UserPlus size={22} color="#000" strokeWidth={1.5} />
        Invite staff member
      </button>

      <VenueBottomNav />
    </div>
  );
}
